import type { EChartsOption } from "echarts";

/**
 * CSV of what a chart shows: one row per x value, one column per named
 * series. Works off the ECharts option every chart already builds, so all of
 * them export the same way.
 *
 * - Series whose name starts with "_" are decorations (festival markers) and
 *   are skipped, as are markPoint/markLine annotations (not in `data`).
 * - A `time` x-axis becomes ISO date + epoch-seconds columns; any other axis
 *   (the compare page's "days since release") is written as-is.
 *
 * Returns null when there is nothing to export.
 */
export function chartOptionsToCsv(options: EChartsOption): string | null {
  const rawSeries = Array.isArray(options.series) ? options.series : options.series ? [options.series] : [];
  const series = rawSeries.filter(
    (s): s is { name: string; data: unknown[] } =>
      !!s &&
      typeof (s as { name?: unknown }).name === "string" &&
      !(s as { name: string }).name.startsWith("_") &&
      Array.isArray((s as { data?: unknown }).data) &&
      ((s as { data: unknown[] }).data.length > 0),
  );
  if (series.length === 0) return null;

  const xAxis = Array.isArray(options.xAxis) ? options.xAxis[0] : options.xAxis;
  const timeAxis = (xAxis as { type?: string } | undefined)?.type === "time";

  const rows = new Map<number, Array<number | null>>();
  series.forEach((s, col) => {
    for (const point of s.data) {
      const tuple = Array.isArray(point)
        ? point
        : point && typeof point === "object" && Array.isArray((point as { value?: unknown }).value)
          ? ((point as { value: unknown[] }).value)
          : null;
      if (!tuple) continue;
      const x = typeof tuple[0] === "number" ? tuple[0] : typeof tuple[0] === "string" ? Date.parse(tuple[0]) : NaN;
      if (!Number.isFinite(x)) continue;
      const y = typeof tuple[1] === "number" && Number.isFinite(tuple[1]) ? tuple[1] : null;
      let row = rows.get(x);
      if (!row) {
        row = new Array<number | null>(series.length).fill(null);
        rows.set(x, row);
      }
      row[col] = y;
    }
  });
  if (rows.size === 0) return null;

  // Header names can be game names (compare charts) — attacker-controlled
  // text. A leading = + - @ (or tab/CR) would run as a formula in Excel or
  // Sheets, so it gets an apostrophe; values are numbers and stay as-is.
  const escape = (v: string) => {
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const header = timeAxis
    ? ["date", "timestamp", ...series.map((s) => escape(s.name))]
    : ["x", ...series.map((s) => escape(s.name))];

  const lines = [header.join(",")];
  for (const x of [...rows.keys()].sort((a, b) => a - b)) {
    const values = rows.get(x)!.map((v) => (v === null ? "" : String(v)));
    lines.push(
      timeAxis
        ? [new Date(x).toISOString(), String(Math.floor(x / 1000)), ...values].join(",")
        : [String(x), ...values].join(","),
    );
  }
  return lines.join("\n");
}

/** Hands the CSV to the browser as a file download. */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
